/** Navigation shared by public records pages and the game's embedded browser.
 *
 * Two exits, never confused: the panel's "Back to game" lives in the GAME
 * document's toolbar (right side), while a page's own back link lives in the
 * page header (left side) and only ever moves inside the boards. Whatever the
 * nesting depth — book → board → run — Escape and the panel button leave. */
export const RECORDS_CLOSE = "pd-records-close";

/** Pages a boards surface may return to. `/board` is a record book's full
 *  list; its query carries the board's filters, so the return is exact. */
const RETURN_PATHS = ["/leaderboard", "/board", "/race", "/run"];

export function recordsReturnPath(value: string | null): string | null {
  if (!value || /[\\\r\n]/.test(value)) return null;
  const url = new URL(value, "https://records.invalid");
  if (url.origin !== "https://records.invalid" || url.hash) return null;
  return RETURN_PATHS.includes(url.pathname) ? url.pathname + url.search : null;
}

export function embeddedRecords(): boolean {
  if (new URLSearchParams(location.search).get("embedded") !== "1" || window.parent === window) return false;
  try { return window.parent.location.origin === location.origin; } catch { return false; }
}

export function closeRecords(): void {
  window.parent.postMessage(RECORDS_CLOSE, location.origin);
}

/** Where a run page's back link goes, in the words the page can show. */
function backLabel(path: string | null): string {
  if (path === null) return "The boards →";
  if (path.startsWith("/race?")) return "← Race result";
  if (path.startsWith("/run?")) return "← Run";
  if (path.startsWith("/board")) return "← Board";
  const view = new URL(path, "https://records.invalid").searchParams.get("view") ?? "rankings";
  return view === "mine" ? "← My floors" : view === "racing" ? "← Keyracing" : view === "recent" ? "← Recent floors" : "← Record book";
}

export function setupRecordsNavigation(): boolean {
  const embedded = embeddedRecords();
  const gameLink = document.getElementById("game-link") as HTMLAnchorElement | null;
  if (embedded) {
    if (gameLink) gameLink.hidden = true; // The panel owns the persistent Back to game control.
    window.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault(); closeRecords();
    });
  }
  const back = document.getElementById("records-back") as HTMLAnchorElement | null;
  if (back) {
    const path = recordsReturnPath(new URLSearchParams(location.search).get("returnTo"));
    const target = new URL(path ?? "/leaderboard", location.origin);
    if (embedded) target.searchParams.set("embedded", "1"); else target.searchParams.delete("embedded");
    back.href = target.pathname + target.search;
    back.textContent = backLabel(path);
  }
  return embedded;
}
